'use client';

import React, { ReactNode } from 'react';
import { useWorkspace } from '@/contexts/WorkspaceProvider';
import { getEnabledProductModules } from '@/views/settings/components/settingsTabAccess';
import { LucideIcon, Lock, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useRouter } from 'next/navigation';
import { PATH } from '@/constants';

type FeatureFlag = 'gtm_signal_enabled' | 'ai_seo_enabled' | 'outbound_enabled' | 'power_dialer_enabled';

interface FeatureGateProps {
  /**
   * The feature flag to check (optional if module is provided)
   */
  feature?: FeatureFlag;
  /**
   * GH#1280: Product module gating — 'marketing' or 'sales'
   */
  module?: 'marketing' | 'sales';
  /**
   * The content to render if the feature is enabled
   */
  children: ReactNode;
  /**
   * Custom title for the access denied state
   */
  title?: string;
  /**
   * Custom description for the access denied state
   */
  description?: string;
  /**
   * Custom icon for the access denied state
   */
  icon?: LucideIcon;
  /**
   * Whether to show a loading state while checking access
   * @default true
   */
  showLoading?: boolean;
}

/**
 * FeatureGate component that conditionally renders children based on feature flag status.
 * Shows an access denied state if the feature is not enabled for the current workspace.
 *
 * Features default to:
 * - gtm_signal_enabled: false
 * - ai_seo_enabled: false
 * - outbound_enabled: true (preserves existing access for existing workspaces)
 */
export const FeatureGate: React.FC<FeatureGateProps> = ({
  feature,
  module: productModule,
  children,
  title = 'Feature Not Available',
  description = 'This feature requires an upgrade. Upgrade to Professional to unlock full access.',
  icon: Icon = Lock,
  showLoading = true,
}) => {
  const workspaceContext = useWorkspace();
  const router = useRouter();

  // Show loading state while workspace context is loading
  if (showLoading && (!workspaceContext || workspaceContext.isLoading)) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] py-12 px-4 text-center">
        <Loader2 className="w-8 h-8 text-slate-400 animate-spin mb-4" />
        <p className="text-sm text-slate-500">Loading...</p>
      </div>
    );
  }

  // Check if feature is enabled
  const isFeatureEnabled = (() => {
    if (!workspaceContext?.activeWorkspace) {
      return false;
    }

    // GH#1280: Check product module first — normalize [] to both-enabled
    if (productModule) {
      const modules = getEnabledProductModules(workspaceContext.activeWorkspace.product_modules);
      if (!modules.includes(productModule)) return false;
    }

    // If no feature flag specified, only module check applies
    if (!feature) return true;

    switch (feature) {
      case 'gtm_signal_enabled':
        return workspaceContext.activeWorkspace.gtm_signal_enabled === true;
      case 'ai_seo_enabled':
        return workspaceContext.activeWorkspace.ai_seo_enabled === true;
      case 'outbound_enabled':
        // outbound_enabled defaults to true if undefined (preserves existing access)
        return workspaceContext.activeWorkspace.outbound_enabled !== false;
      case 'power_dialer_enabled':
        // power_dialer_enabled defaults to false — workspaces opt in explicitly
        return workspaceContext.activeWorkspace.power_dialer_enabled === true;
      default:
        return false;
    }
  })();

  // If feature is enabled, render children
  if (isFeatureEnabled) {
    return <>{children}</>;
  }

  // Render access denied state
  return (
    <div className="flex flex-col items-center justify-center min-h-[400px] py-12 px-4 text-center bg-slate-50">
      <div className="rounded-full bg-slate-100 p-4 mb-4">
        <Icon className="w-10 h-10 text-slate-400" />
      </div>
      <h3 className="text-xl font-semibold text-slate-900 mb-2">{title}</h3>
      <p className="text-sm text-slate-500 max-w-md mb-6">{description}</p>
      <div className="flex items-center gap-3">
        <Button
          onClick={() => router.push('/settings?tab=billing-and-usage')}
        >
          Upgrade Plan
        </Button>
        <Button
          onClick={() => router.push(PATH.SIGNALS)}
          variant="outline"
        >
          Go to Signals
        </Button>
      </div>
    </div>
  );
};

